'use client';

import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle
} from '@components/ui/dialog';
import { isImageContentType, isPdfContentType } from '@files/constants';
import { formatFileSize } from '@files/format-size';
import { useElementWidth } from '@hooks/use-element-width';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@utils/cn';
import { ChevronLeft, ChevronRight, Download, FileText, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import type { AttachmentView } from './AttachmentGallery';
import FileActionsMenu, { downloadUrlFor } from './FileActionsMenu';
import { LazyPdfPages } from './pdf/lazy';

const MAX_PDF_PAGE_WIDTH = 960;
const PDF_BODY_PADDING = 24;

export type FileViewerProps = {
  files: AttachmentView[];
  /** Index into `files`, or null when closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  canDelete?: (file: AttachmentView) => boolean;
  onDelete?: (file: AttachmentView) => unknown;
};

/**
 * In-app viewer for images and PDFs. Fullscreen on phones (always-visible
 * close button, safe-area aware toolbar), centred dialog on desktop.
 */
const FileViewer: FC<FileViewerProps> = ({
  files,
  index,
  onIndexChange,
  onClose,
  canDelete,
  onDelete
}) => {
  const t = useTranslations('files');
  const [bodyRef, bodyWidth] = useElementWidth<HTMLDivElement>();
  const [page, setPage] = useState({ current: 1, total: 0 });
  const [requestedPage, setRequestedPage] = useState<number | undefined>();

  const file = index === null ? null : (files[index] ?? null);
  const open = Boolean(file);
  const isPdf = Boolean(file && isPdfContentType(file.contentType));
  const isImage = Boolean(file && isImageContentType(file.contentType));
  const hasPrev = index !== null && index > 0;
  const hasNext = index !== null && index < files.length - 1;

  useEffect(() => {
    setPage({ current: 1, total: 0 });
    setRequestedPage(undefined);
  }, [file?.id]);

  const goTo = (next: number) => {
    if (next >= 0 && next < files.length) {
      onIndexChange(next);
    }
  };

  const meta = file
    ? [
        isPdf ? t('pdf') : file.contentType,
        file.width && file.height ? `${file.width}×${file.height}` : null,
        file.sizeBytes ? formatFileSize(file.sizeBytes) : null
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const pdfWidth = Math.max(
    0,
    Math.min(MAX_PDF_PAGE_WIDTH, bodyWidth - PDF_BODY_PADDING)
  );

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          onKeyDown={event => {
            if (event.key === 'ArrowLeft' && hasPrev) {
              event.preventDefault();
              goTo((index ?? 0) - 1);
            } else if (event.key === 'ArrowRight' && hasNext) {
              event.preventDefault();
              goTo((index ?? 0) + 1);
            }
          }}
          className={cn(
            'fixed inset-0 z-50 flex h-dvh w-screen flex-col bg-background duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'md:inset-auto md:left-1/2 md:top-1/2 md:h-[90dvh] md:w-[min(96vw,80rem)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-xl md:border md:border-border'
          )}
        >
          <header className="flex items-center gap-2 border-b border-border px-3 py-2 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] md:pt-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <DialogTitle className="truncate text-label font-medium text-foreground">
                {file?.filename ?? ''}
              </DialogTitle>
              <DialogDescription className="truncate text-meta text-muted-foreground">
                {meta}
                {isPdf && page.total
                  ? ` · ${t('page-of', { page: page.current, total: page.total })}`
                  : ''}
              </DialogDescription>
            </div>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('close')}
              >
                <X className="size-5" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <div
            ref={bodyRef}
            className="flex min-h-0 flex-1 overflow-hidden bg-muted/40"
          >
            {file && isImage ? (
              <div className="flex flex-1 items-center justify-center overflow-auto p-2">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={file.url}
                  alt={file.filename}
                  className="max-h-full max-w-full rounded-sm object-contain"
                />
              </div>
            ) : null}
            {file && isPdf && pdfWidth > 0 ? (
              <LazyPdfPages
                url={file.url}
                width={pdfWidth}
                requestedPage={requestedPage}
                onPageChange={(current, total) => setPage({ current, total })}
                loading={
                  <p
                    role="status"
                    className="flex flex-1 items-center justify-center p-6 text-meta text-muted-foreground"
                  >
                    {t('pdf-loading')}
                  </p>
                }
                error={
                  <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
                    <FileText className="size-8 text-muted-foreground" />
                    <p className="text-caption text-muted-foreground">
                      {t('pdf-error')}
                    </p>
                    <Button type="button" variant="outline" asChild>
                      <a href={downloadUrlFor(file)} download={file.filename}>
                        <Download />
                        {t('download')}
                      </a>
                    </Button>
                  </div>
                }
              />
            ) : null}
            {file && !isImage && !isPdf ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
                <FileText className="size-8 text-muted-foreground" />
                <Button type="button" variant="outline" asChild>
                  <a href={downloadUrlFor(file)} download={file.filename}>
                    <Download />
                    {t('download')}
                  </a>
                </Button>
              </div>
            ) : null}
          </div>

          <footer className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 pb-[calc(var(--safe-bottom)+0.5rem)] md:pb-2">
            <div className="flex items-center gap-1">
              {files.length > 1 ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={!hasPrev}
                    onClick={() => goTo((index ?? 0) - 1)}
                    aria-label={t('previous-file')}
                  >
                    <ChevronLeft />
                  </Button>
                  <span className="text-meta whitespace-nowrap text-muted-foreground tabular-nums">
                    {(index ?? 0) + 1} / {files.length}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={!hasNext}
                    onClick={() => goTo((index ?? 0) + 1)}
                    aria-label={t('next-file')}
                  >
                    <ChevronRight />
                  </Button>
                </>
              ) : null}
              {isPdf && page.total > 1 ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={page.current <= 1}
                    onClick={() => setRequestedPage(page.current - 1)}
                    aria-label={t('previous-page')}
                  >
                    <ChevronLeft />
                  </Button>
                  <span
                    className="text-meta whitespace-nowrap text-muted-foreground tabular-nums"
                    aria-label={t('page-of', {
                      page: page.current,
                      total: page.total
                    })}
                  >
                    <span className="hidden sm:inline">
                      {t('page-of', { page: page.current, total: page.total })}
                    </span>
                    <span className="sm:hidden">
                      {page.current}/{page.total}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={page.current >= page.total}
                    onClick={() => setRequestedPage(page.current + 1)}
                    aria-label={t('next-page')}
                  >
                    <ChevronRight />
                  </Button>
                </>
              ) : null}
            </div>
            {file ? (
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="sm" asChild>
                  <a
                    href={downloadUrlFor(file)}
                    download={file.filename}
                    rel="noreferrer"
                  >
                    <Download />
                    <span className="hidden sm:inline">{t('download')}</span>
                  </a>
                </Button>
                <FileActionsMenu
                  file={file}
                  canDelete={canDelete?.(file) ?? false}
                  onDelete={
                    onDelete
                      ? async target => {
                          onClose();
                          await onDelete(target);
                        }
                      : undefined
                  }
                />
              </div>
            ) : null}
          </footer>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default FileViewer;
